import React, { useContext, useCallback, useEffect, useState } from 'react';
import ToolbarCSS from '../../Toolbar.module.css';
import { ColumnTreeWrapper } from './ColumnTreeWrapper/ColumnTreeWrapper';
import { ColumnItem } from './ColumnItem/ColumnItem';
import { CustomPropertiesAnswerJsonKey, FILTERS, ListColumnKey, SELECTED_COLUMNS } from '../../../../shared/utils/constants';
import { FormQuestion, isFormColumnKey, singleTemplateId } from '../../../List/formColumns';
import { loadTemplateQuestions } from '../../../List/templateQuestions';
import { CustomViewContext, DEFAULT_SORT_ORDER } from '../../CustomViewContextProvider';
import { Alert, Text, ThemePrepared } from '@fluentui/react-northstar';
import { themeNames } from '@fluentui/react-teams';
import { useTranslation } from 'react-i18next';
import { appState } from '../../../../AppState';

type PropTypes = {
  globalTheme: ThemePrepared<any>;
  isIdOrRequestSelected: boolean;
  filtersByFeature: (item: any, field: string) => void;
};

export const ColumnsView = ({ globalTheme, isIdOrRequestSelected, filtersByFeature }: PropTypes) => {
  const { customViewStates, setCustomViewStates } = useContext(CustomViewContext);
  const { columns: { selectedColumns } } = customViewStates;
  const filterColumnsByFeature = (item: FEViewColumn) => {
    return filtersByFeature(item, "label");
  };
  const featureRestrictColumns: FEViewColumn[] = SELECTED_COLUMNS.filter(filterColumnsByFeature);
  const {t} = useTranslation();
  const currentState = appState();

  let customPropertyColId = 1000;
  const cpFeatureEnabled = [{id: customPropertyColId, label: CustomPropertiesAnswerJsonKey}].some(filterColumnsByFeature);
  if(cpFeatureEnabled){
    if(!SELECTED_COLUMNS.some(c => c.id > customPropertyColId))
      currentState?.customProperties.filter(p => !p.IsHidden).sort((a, b) => a.Order - b.Order).forEach(cp => {
        featureRestrictColumns.push({id: customPropertyColId + cp.Id, label: cp.Name} as FEViewColumn);
      });
  }

  // A template's form questions are offered when the view's Template filter names exactly that one template - and only
  // if the agent may use it: the questions come from the scoped TicketTemplate set, which returns none otherwise.
  const templateFilter = customViewStates.filters?.find(filter => filter.id === FILTERS.TEMPLATE.id);
  const templatesLicensed = [{ id: ListColumnKey.Template, label: t('ticket-details.edit-ticket-view.template') }].some(filterColumnsByFeature);
  const templateId = templatesLicensed
    ? singleTemplateId(templateFilter?.values.filter(value => value.selected).map(value => value.key) ?? [])
    : null;
  const [formQuestions, setFormQuestions] = useState<{ templateId: number; questions: FormQuestion[] }>();
  useEffect(() => {
    if (!templateId) return;
    let live = true;
    loadTemplateQuestions(templateId).then(questions => {
      if (!live) return;
      setFormQuestions({ templateId, questions });
      // The selected list redraws only for a new array, so saved question columns get their labels this way.
      setCustomViewStates((prev): CustomViewStatesType => ({
        ...prev,
        columns: {
          ...prev.columns,
          selectedColumns: prev.columns.selectedColumns.map(column => {
            const question = questions.find(q => q.key === Number(column.id));
            return question ? { ...column, label: question.label } : column;
          }),
        },
      }));
    });
    return () => { live = false; };
  }, [templateId]);
  const questionsLoading = templateId != null && formQuestions?.templateId !== templateId;
  if (templateId && !questionsLoading)
    formQuestions.questions.forEach(question => featureRestrictColumns.push({ id: question.key, label: question.label }));

  const excludeColsIds = [];
  selectedColumns.forEach(sc => {
    const restrictedCol = featureRestrictColumns.find(fc => sc.id == fc.id);
    if(restrictedCol)
      sc.label = restrictedCol.label;
    // A saved form question waits for its template's questions; once they are in, one no longer asked goes.
    else if (!(questionsLoading && isFormColumnKey(sc.id)))
      excludeColsIds.push(sc.id);
  });
  excludeColsIds.forEach(colId => {
    const colIndex = selectedColumns.findIndex(col => col.id == colId);
    selectedColumns.splice(colIndex, 1);
  });
 
  const isDarkMode = globalTheme.siteVariables.theme === themeNames.Dark;

  const clearSelected = useCallback(() => {
    setCustomViewStates((prev): CustomViewStatesType => ({
      ...prev,
      columns: {
        columnsKeyOrder: [],
        selectedColumns: [],
        sortOrder: DEFAULT_SORT_ORDER,
      },
    }));
  }, []);
  
  return (
    <div className={`p-3 mt-2 ${ToolbarCSS.secondaryToolbarBg}`}>
      <div className='flex justify-between items-center'>
        <h2 className='font-bold my-3'>{t('ticket.ticket-list.configure-column.columns')}</h2>
        <Text content={t('ticket.ticket-list.configure-column.clear')} color='brand' className={`cursor-pointer`} onClick={clearSelected} />
      </div>
      {!isIdOrRequestSelected && (<Alert fitted danger={true} content={t('ticket.ticket-list.configure-column.id-or-request-is-mandatory')} style={{ fontSize: 12, width: '100%', marginBottom: '.5rem' }} />)}
      <div className='grid grid-cols-2 gap-4'>
        <div>
          <div className={`h-80 overflow-auto pt-2.5 pl-2.5 pb-2.5 pr-2.5 rounded-sm ${ToolbarCSS.ColumnContainer}`}>
            <h2 className='mb-3 font-semibold'>{t('ticket.ticket-list.configure-column.columns-footnote')}</h2>
            {featureRestrictColumns.map((column) => {
              const _hasColumn = selectedColumns.some((col) => col.id === column.id);
              return (
                <ColumnItem
                  globalTheme={globalTheme}
                  key={column.id}
                  label={column.label}
                  checked={_hasColumn}
                  _onChange={() => {
                    if (!_hasColumn) {
                      return setCustomViewStates((prev): CustomViewStatesType => ({
                        ...prev,
                        columns: { ...prev.columns, selectedColumns: [...prev.columns.selectedColumns, column] }
                      }));
                    } else {
                      setCustomViewStates((prev): CustomViewStatesType => {
                        return {
                          ...prev,
                          columns: {
                            ...prev.columns,
                            selectedColumns:
                              prev.columns.selectedColumns.filter(
                                (col) => col.id !== column.id
                              ),
                            sortOrder:
                              prev.columns.sortOrder?.orderBy === column.id
                                ? DEFAULT_SORT_ORDER
                                : prev.columns.sortOrder,
                          },
                        };
                      });
                    }
                  }}
                />
              );
            })}
          </div>
        </div>
        <div>
          <ColumnTreeWrapper
            columns={selectedColumns}
            isDarkMode={isDarkMode}
            onDragEndHandler={(_columns) => {
              //
            }}
            filterColumnsByFeature={filterColumnsByFeature}
          />
        </div>
      </div>
    </div>
  );
};
